import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ClientTestimonial = ({ testimonial, isActive, onActivate }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayToggle = () => {
    setIsPlaying(!isPlaying);
    onActivate();
  };

  return (
    <div className={`relative bg-card rounded-xl border transition-all duration-500 overflow-hidden ${
      isActive 
        ? 'border-accent shadow-lg cosmic-glow' 
        : 'border-border hover:border-accent/50'
    }`}>
      {/* Holographic Effect Overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br from-accent/5 to-cosmic-energy/5 transition-opacity duration-500 ${
        isActive ? 'opacity-100' : 'opacity-0'
      }`}></div>
      <div className="relative p-6">
        {/* Client Info */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="relative">
            <Image
              src={testimonial?.avatar}
              alt={testimonial?.name}
              className="w-16 h-16 rounded-full object-cover"
            />
            {isActive && (
              <div className="absolute -inset-1 bg-gradient-to-r from-accent to-cosmic-energy rounded-full blur opacity-30"></div>
            )}
          </div>
          <div>
            <h4 className="font-headline font-bold text-lg text-foreground">
              {testimonial?.name}
            </h4>
            <p className="font-body text-sm text-muted-foreground">
              {testimonial?.role}
            </p>
            <p className="font-body text-xs text-muted-foreground">
              {testimonial?.company}
            </p>
          </div>
        </div>

        {/* Project Context */}
        <div className="mb-4 p-3 bg-surface/30 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Briefcase" size={16} className="text-accent" />
            <span className="font-body font-medium text-sm text-foreground">
              Project: {testimonial?.project}
            </span>
          </div>
          <p className="font-body text-xs text-muted-foreground">
            {testimonial?.projectDescription}
          </p>
        </div>

        {/* Video/Audio Player */}
        <div className="relative mb-4">
          <div className={`aspect-video bg-surface/50 rounded-lg flex items-center justify-center cursor-pointer transition-all duration-300 ${
            isPlaying ? 'bg-accent/10' : 'hover:bg-surface/70'
          }`} onClick={handlePlayToggle}>
            {!isPlaying ? (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center">
                  <Icon name="Play" size={24} className="text-accent ml-1" />
                </div>
                <span className="font-body text-sm text-muted-foreground">
                  Play Testimonial
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center animate-pulse">
                  <Icon name="Pause" size={24} className="text-accent" />
                </div>
                <span className="font-body text-sm text-accent">
                  Playing...
                </span>
              </div>
            )}
          </div>
          
          {/* Duration */}
          <div className="absolute bottom-2 right-2 bg-background/80 px-2 py-1 rounded text-xs text-muted-foreground">
            {testimonial?.duration}
          </div>
        </div>

        {/* Testimonial Quote */}
        <blockquote className="mb-4">
          <p className="font-body text-sm text-foreground italic leading-relaxed">
            "{testimonial?.quote}"
          </p>
        </blockquote>

        {/* Process Highlights */}
        <div className="space-y-2">
          <h5 className="font-body font-semibold text-sm text-foreground">
            Process Highlights:
          </h5>
          <div className="space-y-1">
            {testimonial?.processHighlights?.map((highlight, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Check" size={14} className="text-success" />
                <span className="font-body text-xs text-muted-foreground">
                  {highlight}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Rating */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/20">
          <div className="flex items-center space-x-1">
            {[...Array(5)]?.map((_, i) => (
              <Icon
                key={i}
                name="Star"
                size={16}
                className={i < testimonial?.rating ? 'text-warning fill-current' : 'text-muted-foreground'}
              />
            ))}
          </div>
          <span className="font-body text-xs text-muted-foreground">
            {testimonial?.date}
          </span>
        </div>
      </div>
      {/* Holographic Scan Lines */}
      {isActive && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/5 to-transparent animate-pulse"></div>
        </div>
      )}
    </div>
  );
};

export default ClientTestimonial;